import { useEffect, useLayoutEffect, useState, useMemo, useRef } from "react";
import { getContentSkillRelevancyStatus } from "../../../../enums/ContentSkillRelevancy";
import { UserType } from "../../../../enums/UserType";
import { SortString } from "../../../../helpers/SortString";
import useAlert from "../../../../Hooks/useAlert";
import useAuth from "../../../../Hooks/useAuth";
import useFullLoader from "../../../../Hooks/useFullLoader";
import usePopUp from "../../../../Hooks/usePopUp";
import text from "../../../../locale.en.json";
import { useSkillTableData } from "../../../../Provider/SkillTableProvider";
import { useTableContext } from "../../../../Provider/TableProvider";
import { AuthService } from "../../../../services/AuthService";
import { RoleServices } from "../../../../services/RoleServices";
import { SkillService } from "../../../../services/SkillService";
import { AIStore } from "../../../../store/store_state";
import { CountInit } from "../../../Common/PaginationCustom";
import { getSkillRoleColumns } from "../../ArchitectSkills/SkillContent/SkillContentTable";
import { Skill_Update_Proficiencies_Table, Skill_Update_Proficiencies_TableV2 } from "../SkillMaintainTableElements";
import { Images } from "../../../../assets/images";
import { SkillClassificationStatus } from "../../../../enums/SkillClassificationStatus";
import { isGenAiDisprzAtomicSkillSource } from "../../../../helpers/CommonHelper";
import { skillStatusType } from "../../../../enums/SkillStatusType";

const tabTitle = [
  "Proficiencies",
  "Content",
  "Roles",
  "Personas",
  "Assessments",
];

let selectedContentItemsContainer = [];
let selectedRelevancyItemContainer = {};

const useSkillsManageTable = ({ id, detailData ,levels ,schemeIDData }) => {
  // Context Hooks
  const { showAlert } = useAlert();
  const { setSortAndFilterReset } = useTableContext();
  const { setFullLoader } = useFullLoader();
  const { userDetails } = useAuth();
  const { User_Type } = userDetails;
  const { contentDetailModal, setContentDetailModal } = useSkillTableData();
  const { popUp } = usePopUp();

  // Derived Variables
  const skillSourceId = detailData?.skillSource?.sourceId;
  const isGenAi = isGenAiDisprzAtomicSkillSource(skillSourceId);
  const levelArray = Array.from({ length: levels }, (_, i) => i + 1);
  const levelKey = "selectedFor" + levels + "th";


  // AI Store Hooks
  const aiSuggestedProficienyDescription = AIStore.useState(
    (s) => s.aiSuggestedProficienyDescription
  );
  const aiSuggestedKnowledgeDescription = AIStore.useState(
    (s) => s.aiSuggestedKnowledgeDescription
  );

  // UI State Hooks
  const [currentTab, setCurrentTab] = useState(0);
  const [tabList, setTabList] = useState([]);
  const [profiTableHeight, setProfiTableHeight] = useState("auto");
  const [roletableHeight, setRoletableHeight] = useState("auto");
  const [showIrrelevantCourses, setShowIrrelevantCourses] = useState(false);
  const [isRelevant, setIsRelevant] = useState(false);

  // Table Columns
  const [proficiencyTableColumn, setProficiencyTableColumn] = useState(null);
  const [roleTableColumn, setRoleTableColumn] = useState(getSkillRoleColumns(!isGenAiDisprzAtomicSkillSource(skillSourceId)));

  // Skill Data Hooks
  const [proficiencyArray, setProficiencyArray] = useState(null);
  const [skillRoles, setSkillRoles] = useState(null);
  const [skillRolesCopy, setSkillRolesCopy] = useState(null);
  const [baseSkillRolesCopy, setBaseSkillRolesCopy] = useState(null);
  const [skillContentItems, setSkillContentItems] = useState(null);
  const [skillContentItemsCopy, setSkillContentItemsCopy] = useState(null);
  const [skillPersonas, setSkillPersonas] = useState(null);
  const [skillPersonasCopy, setSkillPersonasCopy] = useState(null);
  const [baseSkillPersonasCopy, setBaseSkillPersonasCopy] = useState(null);
  const [skillAssessmentList, setSkillAssessmentList] = useState(null);
  const [skillAssessmentListCopy, setSkillAssessmentListCopy] = useState(null);
  const [baseSkillAssessmentListCopy, setBaseSkillAssessmentListCopy] = useState(null);
  const [isSavePLDisabled, setIsSavePLDisabled] = useState(true);
  const [levelDataMap, setLevelDataMap] = useState({});
  const [isProficencyUpdate, setIsProficencyUpdate] = useState(false);
  const [proficiencyList, setProficiencyList] = useState([]);
  const descriptionMap = AIStore.useState((s) => s.aiSuggestedProficienyDescriptionV2);
  const knowledgeMap = AIStore.useState((s) => s.aiSuggestedKnowledgeDescriptionV2);

  // Selection & Sorting Hooks
  const [levelMenu, setLevelMenu] = useState(null);
  const [levelMenuRoles, setLevelMenuRoles] = useState([]);
  const [levelMenuRolesMasterList, setLevelMenuRolesMasterList] = useState(null);
  const [subSectors, setSubSectors] = useState(null);
  const [functionList, setFunctionList] = useState([]);
  const [occupationList, setOccupationList] = useState([]);
  const [contentSort, setContentSort] = useState(null);
  const [contentFilters, setContentFilters] = useState(null);

  // Popups & Modals
  const [confirmPopup, setConfirmPopup] = useState(false);
  const [warningPopUp, setWarningPopUp] = useState(false);
  const [showDescriptionLevelPopUp, setShowDescriptionLevelPopUp] = useState({
    proficiencyDescriptionLevel: false,
    knowledgeDescriptionLevel: false,
  });

  // Current Row State
  const [currentProRow, setCurrentProRow] = useState(null);
  const [currentContentItemRow, setCurrentContentItemRow] = useState(null);
  const [currenRoleRow, setCurrenRoleRow] = useState(null);

  // Loading States
  const [contentLoading, setContentLoading] = useState(false);
  const [assessmentGenerated, setAssessmentGenerated] = useState(false);

  //pagination
  const [pageOffset, setPageOffset] = useState(0);
  const [rowCount, setrowCount] = useState(CountInit);


  const prevLevelsRef = useRef(levels);

  useEffect(() => {
    User_Type === UserType.SME
      ? setTabList(["Proficiencies"])
      : setTabList(tabTitle);
  }, [User_Type]);

  useEffect(() => {
    if(currentTab === 1){
      getContentItems(rowCount, pageOffset, showIrrelevantCourses, prevLevelsRef.current !== levels);
      prevLevelsRef.current = levels
    }
  }, [pageOffset, rowCount]);

  useEffect(() => {
    if(currentTab === 1){
        resetContentStates();
        checkAndGetContents();
        resetItemContainers();
    } 
  }, [levels, currentTab]);

  useEffect(() => {
    if(currentTab === 1){
      checkAndGetContents();
    }
  }, [contentSort, contentFilters, showIrrelevantCourses]);

  useEffect(() => {
    if (!!skillSourceId) {
      getStaticList();

      return () => {
        setProficiencyArray(null);
        setSkillRoles(null);
      };
    }
  }, [skillSourceId]);

  useEffect(() => {
      generateProficiencyArray();
  }, [descriptionMap, knowledgeMap ,levels, showDescriptionLevelPopUp]);

  useEffect(() => {
    if (isGenAiDisprzAtomicSkillSource(skillSourceId)) {
      updateGenAiAtomicSkillData();
    }
  }, [levels]);

  useEffect(()=>{
    setCurrentProRow(null);
  },[levels])

  useEffect(() => {
    const totalLevelCount = getTotalLevelCount(schemeIDData);
    const isValid = validateLevelDataMap(levelDataMap, totalLevelCount);
    const descriptionMap = AIStore?.currentState?.aiSuggestedProficienyDescriptionV2;
    const knowledgeMap =  AIStore?.currentState?.aiSuggestedKnowledgeDescriptionV2;
    const isNotEmpty = (obj) => Object.keys(obj).length > 0;
    if(isValid && !isNotEmpty(knowledgeMap["savedFor4th"]?.[0] || {}) && !isNotEmpty(descriptionMap["savedFor4th"]?.[0] || {})){
      setIsSavePLDisabled(false);
      return;
    }
    setIsSavePLDisabled(true);
    const data = [];

    if (schemeIDData?.length){
      schemeIDData.forEach((el)=>{
        const levelKey = "selectedFor" + el?.levelCount + "th";
        const savedLevelKey = "savedFor" + el?.levelCount + "th";

        const des = descriptionMap[levelKey]?.[0] || {};
        const know = knowledgeMap[levelKey]?.[0] || {};

        const desSaved = descriptionMap[savedLevelKey]?.[0] || {};
        const knowSaved = knowledgeMap[savedLevelKey]?.[0] || {};

        const levelArray = Array.from({ length: el?.levelCount }, (_, i) => i + 1);

        levelArray.forEach((level) => {
          const levelName = `Level ${level}`;
          const selectedDesc = des[levelName];
          const selectedKnow = know[levelName];
          const savedDesc = desSaved[levelName];
          const shouldUpdate = (selectedDesc !== savedDesc) && skillAssessmentListCopy?.some((a) => a.assessmentType === "Evaluation" && a.levelName === levelName && a.proficiencySchemeLevelCount === levelArray.length);

          data.push({
            SchemeId: el?.schemeID,
            ProficiencyId: des[`${levelName} ProficiencyId`],
            Description: selectedDesc,
            Knowledge: selectedKnow,
            shouldUpdateEvaluationAssessmentText: shouldUpdate
          });
      });

        if (
          isNotEmpty(des) &&
          isNotEmpty(know) &&
          isNotEmpty(desSaved) &&
          isNotEmpty(knowSaved) &&
          (JSON.stringify(des) !== JSON.stringify(desSaved) || JSON.stringify(know) !== JSON.stringify(knowSaved))
        ) {
          setIsSavePLDisabled(false);
          setIsProficencyUpdate(true);
        }
      })
    }
    setProficiencyList(data);
  }, [levelDataMap, schemeIDData]);

  useEffect(() => {
    if (proficiencyArray != null) {
      setLevelDataMap((prev) => ({
        ...prev,
        [levels]: [proficiencyArray], // append to existing array
      }));
    }
  }, [proficiencyArray]);

  useLayoutEffect(() => {
    if (!!document.querySelector(".MuiTableBody-root tr.MuiTableRow-root")) {
      if (currentTab === 2) {
        setRoletableHeight(
          (document.querySelector(".MuiTableBody-root tr.MuiTableRow-root")
            .offsetHeight +
            8) *
            10 +
            (document.querySelector(".MuiTableRow-head").offsetHeight + 8) +
            8
        );
      } else if (currentTab === 0) {
        setProfiTableHeight(
          (document.querySelector(".MuiTableBody-root tr.MuiTableRow-root")
            .offsetHeight +
            8) *
            10 +
            (document.querySelector(".MuiTableRow-head").offsetHeight + 8) +
            8
        );
      }
    }
  }, [currentTab]);

  const resetContentStates = () => {
    setCurrentContentItemRow(null);
    setContentSort(null);
    setContentFilters(null);
    setSkillContentItems(null);
    setSkillContentItemsCopy(null);
  }

  const checkAndGetContents = () => {
    if(pageOffset !== 0) {
      setPageOffset(0);
    } else {
      getContentItems(rowCount, pageOffset, showIrrelevantCourses, true);
    }
  }

  const generateProficiencyArray = () => {
    if (!Array.isArray(levelMenu)) return;
    const newData = [];
    levelArray.forEach((levelNum, index) => {
      const currentLevelObj = levelMenu.find(
        (item) => item.levelNumber === levelNum
      );
      const des = descriptionMap[levelKey]?.[0] || {};
      const know = knowledgeMap[levelKey]?.[0] || {};
      const desValue = des[`Level ${levelNum}`] || "";
      const knowValue = know[`Level ${levelNum}`] || "";
      if (desValue || knowValue) {
        const pushData = {
          level: `Level ${levelNum}`,
          levelId: currentLevelObj?.value || "",
          levelNumber: levelNum,
          description: desValue,
          knowledge: knowValue,
          ability: "",
          shouldUpdateEvaluationAssessmentText: false,
        };
        newData.push(pushData);
      }
    });
    if (newData.length > 0 && isGenAiDisprzAtomicSkillSource(skillSourceId)) {
      setProficiencyArray(newData);
    }
  };

  const updateGenAiAtomicSkillData = () => {
    if(baseSkillRolesCopy?.length){
      // Filter roles by level
      const skillRolesForSelectedLevel = baseSkillRolesCopy?.filter((s) => s.proficiencySchemeLevelCount === levels)
      setSkillRoles(skillRolesForSelectedLevel);
      setSkillRolesCopy(skillRolesForSelectedLevel);
    }

    // Reset current editing row
    setCurrenRoleRow(null);

    if(levelMenuRolesMasterList?.length){
      // Prepare and set level menu
      const requiredLevels = levelMenuRolesMasterList?.filter((p) => p.proficiencySchemeLevelCount === levels);

      const levelMenu = requiredLevels?.length
        ? SortString(
            requiredLevels.map((el) => ({
              label: el.level,
              value: el.levelId,
              levelNumber: el.levelNumber,
              needId: el.proficiencyId,
            })),
            "levelNumber"
          )
        : [];

      setLevelMenuRoles(levelMenu);
    }
    

    // Filter and set assessments
    if (baseSkillAssessmentListCopy?.length) {
      setAssessmentGenerated(checkAllAssessment(baseSkillAssessmentListCopy));
      const skillAssessmentForSelectedLevel = baseSkillAssessmentListCopy.filter((a) => a.proficiencySchemeLevelCount === levels)
      setSkillAssessmentList(skillAssessmentForSelectedLevel);
      setSkillAssessmentListCopy(skillAssessmentForSelectedLevel);
    }

    if( baseSkillPersonasCopy?.length) {
      // Filter and set personas
      const skillPersonasForSelectedLevel = baseSkillPersonasCopy?.filter((p) => p.proficiencySchemeLevelCount === levels);
      setSkillPersonas(skillPersonasForSelectedLevel);
      setSkillPersonasCopy(skillPersonasForSelectedLevel);
    }
  };

  const handleTab = (val) => {
    setCurrentProRow(null);
    setCurrenRoleRow(null);
    setCurrentContentItemRow(null);
    setCurrentTab(val);
    updateReset();
  };

  const getRelevancyButtonProps = (type) => {
    let isContentSelected = selectedContentItemsContainer?.length > 0;

    if (!showIrrelevantCourses && isContentSelected && type === "relevant") {
      isContentSelected = Object.values(selectedRelevancyItemContainer).includes("Not verified");
    }

    const handleClick = () => {
      setIsRelevant(type === "relevant");
      setConfirmPopup(true);
    };

    return { handleClick, isContentSelected };
  };

  const handlePagination = (e, page) => setPageOffset((page - 1) * rowCount);

  const changeRowCount = (count) => setrowCount(count);

  const getRelevancyLabel = (value) =>
    getContentSkillRelevancyStatus(value, isGenAi);

  const transformContentDetailList = (details = []) =>
    details.map((item) => ({
      ...item,
      relevancy: getRelevancyLabel(item.relevancy),
      checked: selectedContentItemsContainer?.includes(item.contentId),
    }));

  const transformFilters = (filters = []) =>
    filters.map((filter) => {
      if (filter?.name === "relevancy") {
        return {
          ...filter,
          options: filter.options?.map((option) => ({
            ...option,
            label: getRelevancyLabel(option.value),
          })),
        };
      }
      return filter;
    });

  const getContentItems = async (
    rowCountX,
    pageOffsetX,
    showIrrelevant,
    contentLoad
  ) => {
    setContentLoading(!!contentLoad);

    try {
      const resContentItems = await SkillService.getSkillContentItemsV3(
          "/" + Number(id),
          "/contentItems",
          `?pagesize=${rowCountX}`,
          `&offset=${pageOffsetX}`,
          ``,
          `&showIrrelevantCourses=${showIrrelevant}`,
          !!contentSort
            ? `&sortBy=${contentSort?.sortBy || ""}&sortOrder=${
                contentSort?.sortOrder || ""
              }`
            : "",
          !!contentFilters ? contentFilters : "",
          `&proficiencySchemeLevelCount=${isGenAiDisprzAtomicSkillSource(skillSourceId) ? levels : 0}`
        );

      const updateData = {
        ...resContentItems,
        contentDetailList: transformContentDetailList(resContentItems?.contentDetailList),
        filters: transformFilters(resContentItems?.filters),
      };
      setSkillContentItemsCopy(updateData);
      setSkillContentItems(updateData);
    } catch (error) {
      console.log(error);
    } finally {
      setContentLoading(false);
    }
  };


  // ContentItems Edit
  const handleContentItemEdit = (e, item, index) => {
    e.stopPropagation();
    if (currentContentItemRow === null) {
      setCurrentContentItemRow(index);
    } else {
      showAlert("Kindly save the existing Role Mapping!", "info", 1500);
    }
  };

  const handleChangeRelevancy = (e, val, colKey, index, item) => {
    updateContentItemList(e, val, colKey, index);
  };

  const resetItemContainers = () => {
    selectedContentItemsContainer = [];
    selectedRelevancyItemContainer = {};
  };

  const handleSelectedItems = (array, item) => {
    const indexToRemove = array.indexOf(item.contentId);
    if (array?.length < 50) {
      const isItemSelected = array?.includes(item.contentId);
      if (!isItemSelected) {
        array.push(item.contentId);
        if (!showIrrelevantCourses) {
          selectedRelevancyItemContainer[item.contentId] = item.relevancy;
        }
      } else {
        indexToRemove !== -1 && array.splice(indexToRemove, 1);
        if (!showIrrelevantCourses) {
          delete selectedRelevancyItemContainer[item.contentId];
        }
      }
    } else if (array?.length === 50 && array?.includes(item?.contentId)) {
      indexToRemove !== -1 && array.splice(indexToRemove, 1);
    } else {
      setWarningPopUp(true);
    }
  };

  const handleCheckBox = (e, item) => {
    handleSelectedItems(selectedContentItemsContainer, item);
    const isSelected = selectedContentItemsContainer.includes(item.contentId);
    if (selectedContentItemsContainer?.length === 50 && !isSelected) {
      return;
    }
    const arr = [...skillContentItems?.contentDetailList];
    const updatedArr = arr.map((el, i) =>
      el.contentId === item.contentId
        ? { ...el, checked: e.target.checked }
        : { ...el }
    );
    setSkillContentItems({
      ...skillContentItems,
      contentDetailList: updatedArr,
    });
  };

  const handleCheckAll = (e) => {
    const arr = [...skillContentItems?.contentDetailList];
    if (e.target.checked && arr.length > 0) {
      arr.forEach((element) => {
        if (selectedContentItemsContainer.length === 50) {
          setWarningPopUp(true);
        } else if (selectedContentItemsContainer.length < 50) {
          selectedContentItemsContainer.push(element.contentId);
          if (!showIrrelevantCourses) {
            selectedRelevancyItemContainer[element.contentId] =
              element.relevancy;
          }
        }
      });
    } else {
      resetItemContainers();
    }
    const updatedArr = arr.map((el) => ({
      ...el,
      checked: selectedContentItemsContainer?.includes(el?.contentId) || false,
    }));
    setSkillContentItems({
      ...skillContentItems,
      contentDetailList: updatedArr,
    });
  };

  const updateContentItemList = (e, val, colKey, index) => {
    const arr = [...skillContentItems?.contentDetailList];
    const updatedArr = arr.map((el, i) =>
      index === i
        ? {
            ...el,
            [colKey]: e,
          }
        : {
            ...el,
          }
    );
    setSkillContentItems({
      ...skillContentItems,
      contentDetailList: updatedArr,
    });
  };

  const handleContentItemSave = async (e, item, i) => {
    e.stopPropagation();
    setContentLoading(true);
    try {
      await SkillService.bulkUpdateSkillContentItemV3(
        `/${id}`,
        [item.contentId],
        item.relevancy === (isGenAiDisprzAtomicSkillSource(skillSourceId) ? "SA Verified - Yes" :  "Yes"),
        isGenAiDisprzAtomicSkillSource(skillSourceId)
      );
      getContentItems(rowCount, pageOffset, showIrrelevantCourses, false);
    } catch (error) {
      console.log(error);
    } finally {
      setCurrentContentItemRow(null);
      resetItemContainers();
    }
  };

  const handleUpdateContentRelevancy = async () => {
    setConfirmPopup(false);
    setContentLoading(true);
    try {
      if (!showIrrelevantCourses && isRelevant) {
        var hasYesValue = Object.values(
          selectedRelevancyItemContainer
        ).includes(isGenAiDisprzAtomicSkillSource(skillSourceId) ? "SA Verified - Yes" :  "Yes");
        var hasNotVerifiedValue = Object.values(
          selectedRelevancyItemContainer
        ).includes(isGenAiDisprzAtomicSkillSource(skillSourceId) ? "AI Verified" : "Not verified");
        if (hasYesValue && hasNotVerifiedValue) {
          var filteredContentIds = Object.keys(
            selectedRelevancyItemContainer
          ).filter((contentId) => {
            return selectedRelevancyItemContainer[contentId] === (isGenAiDisprzAtomicSkillSource(skillSourceId) ? "AI Verified" : "Not verified");
          });
          selectedContentItemsContainer = filteredContentIds;
        }
      }
      await SkillService.bulkUpdateSkillContentItemV3(
        `/${id}`,
        selectedContentItemsContainer,
        isRelevant,
        isGenAiDisprzAtomicSkillSource(skillSourceId)
      );
      clearSelectedContentItems();
      resetItemContainers();
      await getContentItems(rowCount, pageOffset, showIrrelevantCourses, false);
    } catch (error) {
      console.log(error);
    }
  };

  const clearSelectedContentItems = () => {
    const arr = [...skillContentItems?.contentDetailList];
    const updatedArr = arr.map((el) => ({ ...el, checked: false }));
    setSkillContentItems({
      ...skillContentItems,
      contentDetailList: updatedArr,
    });
  };

  const getStaticList = async () => {
    try {
      const resSubSectors = await AuthService.subSectorListV3();
      const sorted = SortString(resSubSectors, "subsector");
      setSubSectors(
        sorted.map((el) => ({
          label: el.subsector,
          value: el.subsectorId,
          type: el.subsectorTypeId,
        }))
      );

      const resLevels = await SkillService.getSkillProficiencyLevelsV3(
        skillSourceId
      );
      const levelMenu = await resLevels.map((el) => ({
        label: el.level,
        value: el.levelId,
        levelNumber: el.levelNumber,
      }));

      const sortLevels = SortString(levelMenu, "levelNumber") || [];

      setLevelMenu(sortLevels);
      modifyProficiency(sortLevels);
      modifyRoles(levelMenuRoles);

      const resPersonas = await SkillService.getSkillTypesArrayV3(
        Number(id),
        "personas"
      );
      if(isGenAiDisprzAtomicSkillSource(skillSourceId)){
        const skillPersonasForSelectedLevel = resPersonas.filter((p) => p.proficiencySchemeLevelCount === levels);
        setSkillPersonas(skillPersonasForSelectedLevel);
        setSkillPersonasCopy(skillPersonasForSelectedLevel);
        setBaseSkillPersonasCopy(resPersonas);
      } else {
       setSkillPersonas(resPersonas);
       setSkillPersonasCopy(resPersonas);
      }

      getAssessmentList();
    } catch (err) {
      console.log(err);
    }
  };

  const getAssessmentList = async () => {
    const resAssessment = await SkillService.getSkillTypesArrayV3(
      Number(id),
      "assessments"
    );
    const assessmentListMapped = resAssessment.map((el) => ({
      assessmentType: el.type.name,
      assessmentSubType: el.subType.name,
      evaluator: el?.evaluatorType?.name || "",
      ...el,
    }));
    const sortLevels = SortString(assessmentListMapped, "levelName") || [];
    setAssessmentGenerated(checkAllAssessment(sortLevels));
    if(isGenAiDisprzAtomicSkillSource(skillSourceId))
    {
      const skillAssessmentForSelectedLevel = sortLevels.filter((a) => a.proficiencySchemeLevelCount === levels);
      setSkillAssessmentList(skillAssessmentForSelectedLevel);
      setSkillAssessmentListCopy(skillAssessmentForSelectedLevel);
      setBaseSkillAssessmentListCopy(sortLevels);
    } else {
      setSkillAssessmentList(sortLevels);
      setSkillAssessmentListCopy(sortLevels);
    }
  };

  const checkAllAssessment = (arr) => {
    const validEvaluators = ["Manager", "Self", "Reportee", "Peer"];
    const expectedPairs = new Set();

    //Generate expected combinations like: "Level 1|Manager"
    for (let i = 1; i <= levels; i++) {
      for (const evaluator of validEvaluators) {
        expectedPairs.add(`Level ${i}|${evaluator}`);
      }
    }

    const filterArray = arr.filter(
      (el) =>
        el.assessmentType === "Evaluation" &&
        el.assessmentSubType === "Rating(1-5)" &&
        !!el.assessmentText
    );

    const matchedPairs = new Set(
      filterArray.map(el => `${el.levelName}|${el.evaluator}`)
    );

    for (const pair of expectedPairs) {
      if (!matchedPairs.has(pair)) {
        return false;
      }
    }

    return true;
  };

  const modifyRoles = async (menu = []) => {
    try {
      const resRoles = await SkillService.getSkillTypesArrayV3(
        Number(id),
        "roles"
      );

      const insertLevel = await resRoles?.map((role) => ({
        ...role,
        level: role.proficiencyLevel.level,
        levelId: role.proficiencyLevel.levelId,
        levelNumber: role.proficiencyLevel.levelNumber,
        proficiencySchemeLevelCount: role.proficiencyLevel.proficiencySchemeLevelCount
      }));
      if(isGenAiDisprzAtomicSkillSource(skillSourceId)){
        const skillRolesForSelectedLevel = insertLevel.filter((s) => s.proficiencySchemeLevelCount === levels);
        setSkillRoles(skillRolesForSelectedLevel);
        setSkillRolesCopy(skillRolesForSelectedLevel);
        setBaseSkillRolesCopy(insertLevel);
      } else {
        setSkillRoles(insertLevel);
        setSkillRolesCopy(insertLevel);
      }
      const roleTableCol = [...getSkillRoleColumns(!isGenAiDisprzAtomicSkillSource(skillSourceId))];
      const resTable = roleTableCol.map((el) => {
        if (el.select === true && el.colKey === "level") {
          return { ...el, selectMenu: menu };
        } else {
          return { ...el };
        }
      });
      setRoleTableColumn(resTable);
    } catch (err) {
      console.log(err);
    }
  };

  const modifyProficiency = async (menu = []) => {
    try {
      const res = await SkillService.getSkillTypesArrayV3(
        Number(id),
        "proficiencies"
      );
      let prevLevel = [];
      if(isGenAiDisprzAtomicSkillSource(skillSourceId)){
        const requiredLevels = res?.filter((p) => p.proficiencySchemeLevelCount === levels);
          prevLevel = !!requiredLevels
            ? SortString(
                requiredLevels?.map((el) => ({
                  label: el.level,
                  value: el.levelId,
                  levelNumber: el.levelNumber,
                  needId: el.proficiencyId,
                })),
                "levelNumber"
              )
            : [];
      }
      else{
        prevLevel = !!res
          ? SortString(
              res?.map((el) => ({
                label: el.level,
                value: el.levelId,
                levelNumber: el.levelNumber,
                needId: el.proficiencyId,
              })),
              "levelNumber"
            )
          : [];
      }
      
      modifyRoles(prevLevel);
      setLevelMenuRoles(prevLevel);
      setLevelMenuRolesMasterList(res);
      const lMenu = !!menu ? [...menu] : [];
      const filterLevelMenu = lMenu.filter(
        (o1) => !res.some((o2) => o1.value === o2.levelId)
      );
      const profTableCol =  !isGenAiDisprzAtomicSkillSource(skillSourceId) ? Skill_Update_Proficiencies_Table : Skill_Update_Proficiencies_TableV2
      const resTable = profTableCol.map((el) => {
        if (el.select === true) {
          return { ...el, selectMenu: filterLevelMenu };
        } else {
          return { ...el };
        }
      });
      setProficiencyTableColumn(resTable);

      const resProficiency = await SortString(res, "levelNumber").map(
        (el, i) => ({
          ...el,
        })
      );
      
      if(!isGenAiDisprzAtomicSkillSource(skillSourceId)){
        setProficiencyArray(resProficiency);
      }

      getAssessmentList();
    } catch (error) {
      console.log(error);
    }
    
    if (isGenAiDisprzAtomicSkillSource(skillSourceId)) {
      try {
        const result = await getSkillProficienciesWithSchemeBulkAsync();
        if (!levels || !schemeIDData?.length || result?.value?.length == 0) return;
        
        schemeIDData.forEach((schemeItem) => {
          const schemeId = schemeItem.schemeID;

          const matchedProficiency = result?.value.find(
            (item) => item.schemeId === schemeId
          )?.skillProficiencyDetails || [];

          const desMap = {};
          const knowMap = {};

          matchedProficiency.forEach((prof) => {
            desMap[prof.level] = prof.description;
            desMap[prof.level+" ProficiencyId"] = prof.proficiencyId;
            knowMap[prof.level] = prof.knowledge;
            knowMap[prof.level+" ProficiencyId"] = prof.proficiencyId;
          });

          AIStore.update((s) => {
              const key = "selectedFor" + schemeItem.levelCount + "th";
              const savedKey = "savedFor" + schemeItem.levelCount + "th";

            s.aiSuggestedProficienyDescriptionV2[key] = [desMap];
            s.aiSuggestedProficienyDescriptionV2[savedKey] = [desMap];

            s.aiSuggestedKnowledgeDescriptionV2[key] = [knowMap];
            s.aiSuggestedKnowledgeDescriptionV2[savedKey] = [knowMap];
          });
        });
      } catch (error) {
        console.error("Error fetching skill proficiencies:", error);
    }
    }
  };

  // Role Edit
  const handleRoleEdit = (e, item, index) => {
    e.stopPropagation();
    if (currenRoleRow === null) {
    const roleTabArr = [...roleTableColumn].map((el) => {
      if (!!el.select && !!el.selectMenu) {
        if (isGenAiDisprzAtomicSkillSource(skillSourceId)) {
          const requiredLevels = levelMenuRolesMasterList?.filter(
            (p) => p.proficiencySchemeLevelCount === levels
          );

          const levelMenu = !!requiredLevels
            ? SortString(
                requiredLevels.map((el) => ({
                  label: el.level,
                  value: el.levelId,
                  levelNumber: el.levelNumber,
                  needId: el.proficiencyId,
                })),
                "levelNumber"
              )
            : [];

          return {
            ...el,
            selectMenu: levelMenu,
          };
        } else {
          const filteredMenu = SortString(
            [
              {
                label: item.level,
                value: item.level,
                levelNumber: item.levelNumber,
                needId: item.proficiencyId,
              },
              ...el.selectMenu,
            ],
            "levelNumber"
          ).filter((elem) => elem.value !== item.levelId);

          return {
            ...el,
            selectMenu: filteredMenu,
          };
        }
      }

      return el;
    });

    setRoleTableColumn(roleTabArr);
    setCurrenRoleRow(index);
  } else {
      showAlert("Kindly save the existing Role Mapping!", "info", 1500);
    }
  };

  const handleRoleSelect = (e, val, colKey, index, item) => {
    updateRoleList(e, val, colKey, index);
    const getFunctionsList = async () => {
      try {
        const res = await AuthService.functionsListV3(
          e !== 0 ? `?subSectorIds=${e}` : ""
        );
        const sorted = SortString(
          res.filter((el) => !!el.functionName),
          "functionName"
        );
        setFunctionList(
          sorted.map((el) => ({ label: el.functionName, value: el.functionId }))
        );

        getOccupations(sorted);
      } catch (error) {
        console.log(error);
      }
    };

    const getOccupations = async (list) => {
      try {
        const res = await RoleServices.getOccupationsV3(`?subSectorIds=${e}`);
        const sorted = await res.map((el) => !!el && el.trim()).sort();
        setOccupationList(sorted.map((el) => ({ label: el, value: el })));

        const roleTableCol = [...roleTableColumn];
        const resTable = roleTableCol.map((el) => {
          if (el.select === true && el.colKey === "functionName") {
            return {
              ...el,
              selectMenu: list.map((el) => ({
                label: el.functionName,
                value: el.functionId,
              })),
              dynamic: true,
            };
          } else if (el.select === true && el.colKey === "occupation") {
            return {
              ...el,
              selectMenu: sorted.map((el) => ({ label: el, value: el })),
              dynamic: true,
            };
          } else if (el.select === true && el.colKey === "roleName") {
            return {
              ...el,
              selectMenu: [],
              dynamic: true,
            };
          } else {
            return { ...el };
          }
        });
        setRoleTableColumn(resTable);
      } catch (error) {
        console.log(error);
      }
    };

    const getIndustryList = async () => {
      const initData = {
        roleName: "",
        subsectorIds: [item.subsector],
        functionIds: [e],
        occupations: [],
        roleStatuses: [],
        sourceIds: [skillSourceId]
      };
      try {
        const res = await RoleServices.searchRoleV3(initData);
        const filterRoles = res?.roles.filter(
          (role) =>
            !skillRoles.some((element) => element.roleId === role.roleId)
        );
        const roleTableCol = [...roleTableColumn];
        const resTable = roleTableCol.map((el) => {
          if (el.select === true && el.colKey === "roleName") {
            return {
              ...el,
              selectMenu: SortString(filterRoles, "roleName").map((el) => ({
                label: el.roleName,
                value: el.roleId,
              })),
              dynamic: true,
            };
          } else {
            return { ...el };
          }
        });
        setRoleTableColumn(resTable);
      } catch (error) {
        console.log(error);
      }
    };
    if (colKey === "subsector") {
      getFunctionsList();
    } else if (colKey === "functionName") {
      getIndustryList();
    }
  };

  const updateRoleList = (e, val, colKey, index, item) => {
    const arr = [...skillRoles];
    const updatedArr = arr.map((el, i) =>
      index === i
        ? colKey === "level"
          ? {
              ...el,
              [colKey]: e,
              needId:
                val?.selectMenu.find((el) => el.value === e)?.needId ||
                item?.proficiencyId,
            }
          : { ...el, [colKey]: e }
        : { ...el }
    );
    setSkillRoles(updatedArr);
  };

  const handleRoleSave = async (e, item, i) => {
    e.stopPropagation();

    const { roleId, roleName, needId, proficiencyId, level, proficiencySchemeLevelCount } = item;

    if (!needId || roleName === "" || level === "") {
      showAlert(
        "Industry Role and Proficiency Level selection is required!",
        "info",
        1500
      );
      return;
    }

    const url = `/${roleId === 0 ? roleName : roleId}`;
    const subEntityUrl = "/proficiency/";
    const method = roleId === 0 ? "POST" : "PATCH";
    const postData = { proficiencyId: needId, skillCriticality: "", proficiencySchemeLevelCount: isGenAiDisprzAtomicSkillSource(skillSourceId) ? proficiencySchemeLevelCount : 0};
    const subEntityId = roleId === 0 ? "" : proficiencyId;

    try {
      await RoleServices.addUpdateRoleSkillV3(
        url,
        subEntityUrl,
        subEntityId,
        postData,
        method
      );
      updateReset();
    } catch (error) {
      showAlert(error.error, "error", 1500);
    }
  };

  const handleRoleDelete = async (item) => {
    try {
      await RoleServices.deleteRoleSkillV3(
        "/" + item.roleId,
        "/proficiency/",
        item?.proficiencyId
      );
      showAlert("Successfully deleted", "success", 1500);
      setSortAndFilterReset(true);
      modifyRoles([]);
    } catch (error) {
      showAlert(error.error, "error", 1500);
    }
  };

  // Proficiancy Edit
  const handleProficiencyEdit = (e, item, index) => {
    e.stopPropagation();

    if (!currentProRow) {
      const proTabArr = [...proficiencyTableColumn].map((el) =>
        !!el.select
          ? {
              ...el,
              selectMenu: SortString(
                [
                  {
                    label: item.level,
                    value: item.level,
                    levelNumber: item.levelNumber,
                  },
                  ...el.selectMenu,
                ],
                "levelNumber"
              ),
            }
          : el
      );
      setProficiencyTableColumn(proTabArr);
      setCurrentProRow(index);
    } else {
      showAlert("Kindly save the existing Proficiency Mapping!", "info", 1500);
    }
  };

  const updateReset = () => {
    setCurrentProRow(null);
    setCurrenRoleRow(null);
    setCurrentContentItemRow(null);
    if (currentTab === 0) {
      modifyProficiency(levelMenu); //Proficiencies
    } else if (currentTab === 2) {
      modifyRoles(levelMenuRoles); // Roles
    }
  };

  const handleProficiencyDelete = async (item) => {
    try {
      await SkillService.deleteSkillTypeItemV3(
        "/" + Number(id),
        "/proficiencies/",
        item.proficiencyId
      );
      showAlert("Successfully deleted", "success", 1500);
      modifyProficiency(levelMenu);
    } catch (error) {
      showAlert(error.error, "error", 1500);
    }
  };

  const handleColChange = (e, val, colKey, index) => {
    updateList(e, val, colKey, index);
  };

  const handleProSelect = (e, val, colKey, index) => {
    updateList(e, val, colKey, index);
  };

  const updateList = (e, val, colKey, index) => {
    const arr = [...proficiencyArray];
    const levelObject = val?.selectMenu?.find((el) => el.value === e) || {};
    const updatedArr = arr.map((el, i) => {
      if (index === i) {
        return {
          ...el,
          [colKey]: colKey !== "level" ? e.target.value : e,
          levelName: colKey !== "level" ? el?.levelName : levelObject?.label,
          levelNumber:
            colKey !== "level" ? el?.levelNumber : levelObject?.levelNumber,
          levelId: colKey !== "level" ? el?.levelId : levelObject?.value,
        };
      } else {
        return { ...el };
      }
    });
    setProficiencyArray(updatedArr);
  };

  const addOrUpdateProficiency = async (
    item,
    shouldUpdateEvaluationAssessmentText = false
  ) => {
    const { buttonDelete, buttonEdit, levelName, ...postData } = item;
    const addUpdateAPITrigger = async () => {
      if (!!item.proficiencyId) {
        try {
          await SkillService.updateProficiencyV3(
            "/" + Number(id),
            "/proficiencies/",
            Number(postData.proficiencyId),
            {
              ...postData,
              level: levelName,
              shouldUpdateEvaluationAssessmentText,
            }
          );
          updateReset();
        } catch (error) {
          showAlert(error.error, "error", 1500);
        }
      } else {
        try {
          await SkillService.addProficiencyV3(
            "/" + Number(id),
            "/proficiencies/",
            { ...postData, level: levelName }
          );
          updateReset();
        } catch (error) {
          showAlert(error.error, "error", 1500);
        }
      }
    };
    if (!!postData.levelId) {
      await addUpdateAPITrigger();
    } else {
      showAlert("Proficiency Level selection is required!", "info", 1500);
    }
  };

  const handleProficiencySave = async (e, item) => {
    e.stopPropagation();
    const key = "selectedFor" + levels + "th";
    if (isGenAiDisprzAtomicSkillSource(skillSourceId)) {
      AIStore.update((s) => {
        const existingDescription = s.aiSuggestedProficienyDescriptionV2[key]?.[0] || {};
        const updatedDescription = { ...existingDescription };

        // Append new description to the corresponding level
        const levelKeyDescription = item.level; // e.g. 'Level 2'
        updatedDescription[levelKeyDescription] = item.description;

        s.aiSuggestedProficienyDescriptionV2[key] = [updatedDescription];

        const existingKnowledge = s.aiSuggestedKnowledgeDescriptionV2[key]?.[0] || {};
        const updatedKnowledge = { ...existingKnowledge };

        // Append new description to the corresponding level
        const levelKeyKnowledge = item.level; // e.g. 'Level 2'
        updatedKnowledge[levelKeyKnowledge] = item.knowledge;
        s.aiSuggestedKnowledgeDescriptionV2[key] = [updatedKnowledge];
      });
      setCurrentProRow(null);
      return;
    }
    if (!!item.proficiencyId) {
      var res = await SkillService.hasEvaluationAssessment(
        Number(id),
        item.proficiencyId
      );
      if (res.value) {
        popUp.confirm(
          text.skill.evaluationQuestionSyncConfirmationText,
          "",
          async () => await addOrUpdateProficiency(item, true)
        );
        return;
      }
    }
    addOrUpdateProficiency(item);
  };

  const handleAddPro = () => {
    const arr = [...proficiencyArray];
    const pushData = {
      level: "",
      levelId: "",
      levelNumber: "",
      description: "",
      knowledge: "",
      ability: "",
    };

    if (
      proficiencyArray.length > 0 &&
      !!proficiencyArray[proficiencyArray.length - 1].proficiencyId
    ) {
      arr.push(pushData);
      setProficiencyArray(arr);
      setCurrentProRow(arr.length - 1);
    } else if (proficiencyArray.length === 0) {
      arr.push(pushData);
      setProficiencyArray(arr);
      setCurrentProRow(arr.length - 1);
    } else {
      showAlert("You must save last Proficiency", "info", 1500);
    }
  };

  const handleAddRole = () => {
    if (skillRoles.length > 0 && !!skillRoles[skillRoles.length - 1].roleId) {
      makeSkillRole();
    } else if (skillRoles.length === 0) {
      makeSkillRole();
    } else {
      showAlert(
        "Kindly save the existing Role Mapping before adding another!",
        "info",
        2500
      );
    }
  };

  const makeSkillRole = () => {
    const arr = [...skillRoles];

    const pushData = {
      subsector: "",
      functionName: "",
      occupation: "",
      roleName: "",
      roleId: 0,
      level: "",
      levelId: 0,
    };

    arr.push(pushData);
    setSkillRoles(arr);
    setCurrenRoleRow(arr.length - 1);

    const roleTableCol = [...getSkillRoleColumns(!isGenAiDisprzAtomicSkillSource(skillSourceId))];
    const resTable = roleTableCol.map((el) => {
      if (el.select === true && el.colKey === "level") {
        return { ...el, selectMenu: levelMenuRoles };
      } else if (el.select === true && el.colKey === "subsector") {
        return { ...el, selectMenu: subSectors, dynamic: true };
      } else if (el.select === true && el.colKey === "functionName") {
        return { ...el, selectMenu: functionList, dynamic: true };
      } else if (el.select === true && el.colKey === "occupation") {
        return { ...el, selectMenu: occupationList, dynamic: true };
      } else if (el.select === true && el.colKey === "roleName") {
        return { ...el, selectMenu: [], dynamic: true };
      } else {
        return { ...el };
      }
    });
    setRoleTableColumn(resTable);

    const tableLastRow = document.querySelector(".lastRow");
    setTimeout(() => {
      tableLastRow.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 100);
  };

  const handleAiGenerator = (el) => {
    if (el.colValue === "Description") {
      setShowDescriptionLevelPopUp({ proficiencyDescriptionLevel: true });
    } else if (el.colValue === "Knowledge") {
      setShowDescriptionLevelPopUp({ knowledgeDescriptionLevel: true });
    }
  };

  const handleAiGeneratorNoClick = () => {
    setShowDescriptionLevelPopUp({
      proficiencyDescriptionLevel: false,
      knowledgeDescriptionLevel: false,
    });
  };

  const addOrUpdateProficiencies = async (levelsToBeUpdated) => {
    try {
      setFullLoader(true);
      const validProficiencyLevels = levelsToBeUpdated.filter(Boolean);
      for (const item of validProficiencyLevels) {
        item?.proficiencyId
          ? await SkillService.updateProficiencyV3(
              "/" + Number(id),
              "/proficiencies/",
              Number(item.proficiencyId),
              { ...item }
            )
          : await SkillService.addProficiencyV3(
              "/" + Number(id),
              "/proficiencies/",
              { ...item }
            );
      }
      updateReset();
      showAlert("Level descriptions updated successfully", "success", 1500);
      handleAiGeneratorNoClick();
    } catch (errors) {
      showAlert(errors, "error", 1500);
      updateReset();
      handleAiGeneratorNoClick();
      console.log("error", errors);
    } finally {
      setFullLoader(false);
    }
  };

  ////TODO: Once the genai option is available for 1.1 source skills to generate PL modifiy the below accordingly & use it & it should not affect the older source functionality
  // const handleAiGeneratorYesClick = async (levels, level) => {
  //   const key = "selectedFor" + level + "th";
  //   AIStore.update((s) => {
  //     if (showDescriptionLevelPopUp.proficiencyDescriptionLevel) {
  //       const existing = s.aiSuggestedProficienyDescriptionV2[key]?.[0] || {};
  //       const merged = { ...existing };

  //       // Merge by appending text for each level
  //       Object.keys(levels).forEach((lvlKey) => {
  //         merged[lvlKey] = (existing[lvlKey] || "") + " " + levels[lvlKey];
  //       });
  //       s.aiSuggestedProficienyDescriptionV2[key] = [merged];
  //     } else {
  //       const existing = s.aiSuggestedKnowledgeDescriptionV2[key]?.[0] || {};
  //       const merged = { ...existing };

  //       // Merge by appending text for each level
  //       Object.keys(levels).forEach((lvlKey) => {
  //         merged[lvlKey] = (existing[lvlKey] || "") + " " + levels[lvlKey];
  //       });
  //       s.aiSuggestedKnowledgeDescriptionV2[key] = [merged];
  //     }
  //   });
  //   showAlert("Level descriptions updated successfully", "success", 1500);
  //   handleAiGeneratorNoClick();
  // };

  const handleAiGeneratorYesClick = async (levels) => {
    const updatedProficiencyArray = [...proficiencyArray];
    const levelsToBeUpdated = Object.keys(levels).map(async (level, i) => {
      const description = levels[level];
      let item = updatedProficiencyArray?.find((item) => item.level === level);
      let shouldUpdateEvaluationAssessmentText = false;
      if (item?.proficiencyId) {
        let res = await SkillService.hasEvaluationAssessment(
          Number(id),
          Number(item.proficiencyId)
        );
        shouldUpdateEvaluationAssessmentText = res.value;
      }
      if (item && description) {
        showDescriptionLevelPopUp.proficiencyDescriptionLevel
          ? (item.description = item?.description
              ? item.description + " " + description
              : description)
          : (item.knowledge = item?.knowledge
              ? item.knowledge + " " + description
              : description);
        item.shouldUpdateEvaluationAssessmentText =
          shouldUpdateEvaluationAssessmentText;
      } else if (description) {
        const levelInfo = levelMenu?.find(
          (sourceLevel) => sourceLevel?.label === level
        );
        item = {
          level: levelInfo?.label ?? "",
          levelId: levelInfo?.value ?? "",
          levelNumber: levelInfo?.levelNumber ?? "",
          description: showDescriptionLevelPopUp.proficiencyDescriptionLevel
            ? description
            : "",
          knowledge: showDescriptionLevelPopUp.knowledgeDescriptionLevel
            ? description
            : "",
          ability: "",
          shouldUpdateEvaluationAssessmentText:
            shouldUpdateEvaluationAssessmentText,
        };
      }
      return item;
    });

    Promise.all(levelsToBeUpdated).then(async (results) => {
      if (results.some((i) => i?.shouldUpdateEvaluationAssessmentText)) {
        popUp.confirm(
          text.skill.evaluationQuestionSyncConfirmationText,
          "",
          async () => await addOrUpdateProficiencies(results)
        );
        return;
      }
      await addOrUpdateProficiencies(results);
    });
  };

  const updateLevelsDescriptions = (
    updatedDescriptionValues,
    updatedKnowledgeDescriptionValues,
    level
  ) => {
    AIStore.update((s) => {
      if (showDescriptionLevelPopUp.proficiencyDescriptionLevel) {
        // Update the array version
        s.aiSuggestedProficienyDescription = [
          ...s.aiSuggestedProficienyDescription,
          updatedDescriptionValues,
        ];

        // Update the object version
        const existing = s.aiSuggestedProficienyDescriptionV2[level] || [];
        s.aiSuggestedProficienyDescriptionV2[level] = [
          ...existing,
          updatedDescriptionValues,
        ];
      } else {
        // Update the array version
        s.aiSuggestedKnowledgeDescription = [
          ...s.aiSuggestedKnowledgeDescription,
          updatedKnowledgeDescriptionValues,
        ];

        // Update the object version
        const existing = s.aiSuggestedKnowledgeDescriptionV2[level] || [];
        s.aiSuggestedKnowledgeDescriptionV2[level] = [
          ...existing,
          updatedKnowledgeDescriptionValues,
        ];
      }
    });
  };

  const getStoreKey = () => {
    if (showDescriptionLevelPopUp.proficiencyDescriptionLevel) {
      return "aiSuggestedProficienyDescription";
    } else if (showDescriptionLevelPopUp.knowledgeDescriptionLevel) {
      return "aiSuggestedKnowledgeDescription";
    }
    return "";
  };

  const handleShowIrrelevantCoursesChange = () => {
    setCurrentContentItemRow(null);
    setPageOffset(0);
    setShowIrrelevantCourses((prev) => !prev);
    resetItemContainers();
  };

  const handleContentTableCancelClick = () => {
    setSkillContentItems(skillContentItemsCopy);
    setCurrentContentItemRow(null);
    resetItemContainers();
  };

  const handleContentTableResetRow = () => {
    setCurrentContentItemRow(null);
  };

  const handleContentTableSort = (value) => {
    setContentSort(value);
  };

  const handleContentTableFilterValues = (value) => {
    setContentFilters(value);
  };

  const handleRoleTableSort = (sortValue, sortBy) => {
    setCurrenRoleRow(null);
    setSkillRoles(SortString(skillRoles, sortValue, sortBy));
  };

  const handleRoleTableResetList = () => {
    setCurrenRoleRow(null);
    if (isGenAiDisprzAtomicSkillSource(skillSourceId)) {
      const skillRolesForSelectedLevel = skillRolesCopy.filter((s) => s.proficiencySchemeLevelCount === levels);
      setSkillRoles(skillRolesForSelectedLevel);
    } else {
      setSkillRoles(skillRolesCopy);
    }
  };

  const handleRoleTableResetRow = () => {
    setCurrenRoleRow(null);
  };

  const handlePersonaTableSort = (sortValue, sortBy) => {
    setSkillPersonas(SortString(skillPersonas, sortValue, sortBy));
  };

  const handlePersonaTableResetList = () => {
    if(isGenAiDisprzAtomicSkillSource(skillSourceId)){
      setSkillPersonas(skillPersonasCopy.filter((p) => p.proficiencySchemeLevelCount === levels));
    } else {
      setSkillPersonas(skillPersonasCopy);
    }
  };

  const handleCloseContentDetailModal = () => {
    setContentDetailModal(false);
  };

  const handleCloseWarningPopup = () => {
    setWarningPopUp(false);
  };

  let contentStates = [
    {
      condition: detailData?.classificationStatus === SkillClassificationStatus.IN_PROGRESS || detailData?.classificationStatus === SkillClassificationStatus.SKILL_MODEL_CREATION_TRIGERRED || detailData?.classificationStatus === SkillClassificationStatus.SKILL_CLASSIFICATION_TRIGERRED,
      icon: Images.icons.importProgress,
      subtitle: text.content.importingContent,
    },
    {
      condition: proficiencyArray?.length && proficiencyArray.some((el) => !el.description || !el.knowledge),
      icon: Images.icons.emptyFolder,
      title: text.content.completeProficiencyAndKnowledgeDescription,
      subtitle: text.content.contentTaggingRequirementMessage,
    },
    {
      condition: detailData?.classificationStatus === SkillClassificationStatus.NOT_STARTED && detailData?.skillStatus !== skillStatusType.PUBLISHED,
      icon: Images.icons.notFound,
      title: text.content.noContentMapped,
      subtitle: text.content.classificationTriggerInfo,
    },
    {
      condition: detailData?.classificationStatus === SkillClassificationStatus.NOT_STARTED || detailData?.classificationStatus === SkillClassificationStatus.NO_CONTENTS_MAPPED || detailData?.classificationStatus === SkillClassificationStatus.NO_CONTENTS_AVAILABLE,
      icon: Images.icons.notFound,
      title: text.content.noContentMapped,
      subtitle: text.content.noRelevantContentAvailableForSkill,
    },
    {
      condition: detailData?.classificationStatus === SkillClassificationStatus.ERROR_GENERATING_SKILL_MODEL || detailData?.classificationStatus === SkillClassificationStatus.ERROR_SUBMITTING_SKILL_FOR_CLASSIFICTION || detailData?.classificationStatus === SkillClassificationStatus.ERROR_SAVING_SKILL_CONTENT_MAPPING,
      icon: Images.icons.errorBlue,
      title: text.content.errorOccuredDuringClassification,
      subtitle: text.content.errorMessageForSkillClassification,
      width: "140px",
      height: "155px"
    },
  ];

  const createSkillProficienciesWithSchemeBulkAsync = async (levelDataMap, schemeIDData) => {
    setFullLoader(true);
    const data = [];
    Object.keys(levelDataMap).forEach((levelKey) => {
      const levelArray = levelDataMap[levelKey];

      levelArray[0].forEach((item) => {
        data.push({
          schemeId: schemeIDData.find((el)=>el.levelCount==levelKey)?.schemeID, // Provided from function param
          description: item.description || "",
          knowledge: item.knowledge || "",
          ability: item.ability || "",
          shouldUpdateEvaluationAssessmentText:
            item.shouldUpdateEvaluationAssessmentText ?? false,
          level: item.level || "", // e.g., "Level 1"
          levelId: item.levelId || 0,
          levelNumber: item.levelNumber || 0,
        });
      });
    });

    try {
      await SkillService.createSkillProficienciesWithSchemeBulkAsync(data, Number(id));
    } catch (errors) {
      console.log("error", errors);
    } finally {
      setFullLoader(false);
    }
  };

  const getSkillProficienciesWithSchemeBulkAsync = async () => {
    try {
      return await SkillService.getSkillProficienciesWithSchemeBulkAsync(Number(id));
    } catch (errors) {
      console.log("error", errors);
    }
  };

  const handleBulkProficienciesUpdate = async (proficiencyList) => {
    setFullLoader(true);
    try {
      await SkillService.updateSkillProficienciesWithSchemeBulkAsync(proficiencyList, Number(id));
    } catch (errors) {
      console.log("error", errors);
    } finally {
      setFullLoader(false);
    }
  }

  const updateSkillProficienciesWithSchemeBulkAsync = async (proficiencyList) => {
    const proceedWithUpdate = async () => {
      await handleBulkProficienciesUpdate(proficiencyList);
      setTimeout(modifyProficiency, 1000);
    };

    const hasUpdates = proficiencyList.some(p => p.shouldUpdateEvaluationAssessmentText);
    if (hasUpdates)
      popUp.confirm(text.skill.evaluationQuestionSyncConfirmationText, "", proceedWithUpdate);
    else
      await proceedWithUpdate();
  };

  const validateLevelDataMap = (dataMap, expectedCount) => {
    const keys = Object.keys(dataMap);
    let validCount = 0;

    for (const levelKey of keys) {
      const levelArray = dataMap[levelKey];
      const items = Array.isArray(levelArray[0]) ? levelArray[0] : levelArray;

      if (!Array.isArray(items)) return false;

      for (const item of items) {
        validCount++
        if (!item?.description?.trim() || !item?.knowledge?.trim()) {
          return false;
        }
      }
    }
    return validCount === expectedCount;
  };

  const getTotalLevelCount = (schemeIDData = []) => {
    return schemeIDData.reduce((total, entry) => {
      const count = Number(entry?.levelCount || 0);
      return total + (isNaN(count) ? 0 : count);
    }, 0);
  };

  const getSkillLevelSuggestionParams = () => ({
      SkillName: detailData.skillName ?? "",
      EmbeddingDescription: detailData.embeddingDescription,
      Levels: levels,
      SkillFamily: detailData.skillFamily?.skillFamily ?? ""
  });

  const getServiceUrl = useMemo(() => {
    return () => {
      return levels > 0
        ? SkillService.GetAiSkillLevelDescriptionSuggestionV3(getSkillLevelSuggestionParams())
        : null;
    };
  }, [levels, detailData]);

  
  if (!!proficiencyTableColumn && !proficiencyArray && isGenAiDisprzAtomicSkillSource(skillSourceId)) {
    setProficiencyArray([]);
  }
  
  return {
    tabList,
    handleTab,
    currentTab,
    skillSourceId,
    proficiencyTableColumn,
    proficiencyArray,
    handleColChange,
    currentProRow,
    handleProSelect,
    handleProficiencyEdit,
    handleProficiencySave,
    handleProficiencyDelete,
    updateReset,
    profiTableHeight,
    handleAiGenerator,
    handleAddPro,
    User_Type,
    showIrrelevantCourses,
    handleShowIrrelevantCoursesChange,
    getRelevancyButtonProps,
    confirmPopup,
    isRelevant,
    handleUpdateContentRelevancy,
    setConfirmPopup,
    contentLoading,
    skillContentItems,
    skillContentItemsCopy,
    currentContentItemRow,
    handleChangeRelevancy,
    handleCheckBox,
    handleCheckAll,
    handleContentItemEdit,
    handleContentItemSave,
    handlePagination,
    rowCount,
    changeRowCount,
    contentStates,
    contentFilters,
    skillRolesCopy,
    roleTableColumn,
    skillRoles,
    currenRoleRow,
    handleRoleSelect,
    handleRoleEdit,
    handleRoleSave,
    handleRoleDelete,
    roletableHeight,
    setSkillRoles,
    skillPersonasCopy,
    skillPersonas,
    setSkillPersonas,
    skillAssessmentList,
    skillAssessmentListCopy,
    levelMenuRoles,
    getAssessmentList,
    assessmentGenerated,
    showDescriptionLevelPopUp,
    handleAiGeneratorNoClick,
    getStoreKey,
    updateLevelsDescriptions,
    contentDetailModal,
    warningPopUp,
    setWarningPopUp,
    handleAddRole,
    setSkillAssessmentList,
    handleAiGeneratorYesClick,
    handleContentTableCancelClick,
    handleContentTableResetRow,
    handleContentTableSort,
    handleContentTableFilterValues,
    handleRoleTableSort,
    handleRoleTableResetList,
    handleRoleTableResetRow,
    handlePersonaTableSort,
    handlePersonaTableResetList,
    handleCloseContentDetailModal,
    handleCloseWarningPopup,
    levelMenu,
    createSkillProficienciesWithSchemeBulkAsync,
    getSkillProficienciesWithSchemeBulkAsync,
    updateSkillProficienciesWithSchemeBulkAsync,
    setProficiencyArray,
    setCurrenRoleRow,
    setCurrentProRow,
    setIsSavePLDisabled,
    isSavePLDisabled,
    generateProficiencyArray,
    AIStore,
    modifyProficiency,
    getServiceUrl,
    isProficencyUpdate,
    proficiencyList
  };
};

export default useSkillsManageTable;